import React, { useId, useMemo } from 'react';

interface NotationSymbolIconProps {
  viewBox: string;
  markup: string;
  size?: number;
  className?: string;
  /** Prefix pattern ids so multiple icons on one page do not collide. */
  idPrefix?: string;
  /** Stretch glyph to fill the icon box (default true for droppable nodes/markers). */
  stretch?: boolean;
}

/**
 * Renders monochrome notation glyphs (DMN / VSM) from visual-reference SVG markup.
 */
export const NotationSymbolIcon: React.FC<NotationSymbolIconProps> = ({
  viewBox,
  markup,
  size = 40,
  className,
  idPrefix,
  stretch = true,
}) => {
  const reactId = useId().replace(/:/g, '');
  const prefix = idPrefix ?? `ns-${reactId}`;

  const safeMarkup = useMemo(() => {
    return markup
      .replace(/\bid="([^"]+)"/g, (_m, id: string) => `id="${prefix}-${id}"`)
      .replace(/url\(#([^)]+)\)/g, (_m, id: string) => `url(#${prefix}-${id})`);
  }, [markup, prefix]);

  const fillParent = Boolean(className && (className.includes('w-full') || className.includes('h-full')));

  return (
    <svg
      viewBox={viewBox}
      width={fillParent ? '100%' : size}
      height={fillParent ? '100%' : size}
      className={className}
      preserveAspectRatio={stretch ? 'none' : 'xMidYMid meet'}
      aria-hidden
      style={fillParent ? { display: 'block', width: '100%', height: '100%' } : { display: 'block' }}
      dangerouslySetInnerHTML={{ __html: safeMarkup }}
    />
  );
};

export default NotationSymbolIcon;
